// src/components/operationCenter/widgets/SpreadRatioWidget.tsx
// Price Ratio (X/Y) chart with log-price transformation toggle.
// Displays raw_value from the signal series which represents the underlying spread/ratio.

import React, { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps } from "@/types/dashboard";
import type { SignalChartConfig } from "@/types/signals";
import { useSignalSeries } from "@/hooks/signals/useSignalSeries";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  createGrid,
  createXAxis,
  createYAxis,
  createLineSeries,
  createTooltip,
  CHART_COLORS,
} from "@/utils/charts/theme";
import { formatDateLabel } from "@/utils/charts/dataUtils";

function formatTooltipDate(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} — ${hh}:${mm}`;
}

const SpreadRatioWidget: React.FC<WidgetProps> = ({ widget }) => {
  const [logScale, setLogScale] = useState(false);

  const config = widget.config as SignalChartConfig;
  const signalName = "ratio";

  const { data, isLoading, error } = useSignalSeries({
    signalName,
    ...(config.run_id != null ? { runId: config.run_id } : {}),
  });

  const { dates, values, currentValue } = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return {
        dates: [] as string[],
        values: [] as (number | null)[],
        currentValue: null,
      };
    }
    const points = data.data;
    const rawValues = points.map((p) => p.signal_value);
    const transformed = logScale
      ? rawValues.map((v) => (v != null && v > 0 ? Math.log(v) : null))
      : rawValues;
    const last = transformed[transformed.length - 1];
    return {
      dates: points.map((p) => p.ts),
      values: transformed,
      currentValue: last ?? null,
    };
  }, [data, logScale]);

  const chartOption = useMemo((): EChartsOption => {
    if (dates.length === 0) return {};

    return {
      backgroundColor: "transparent",
      tooltip: createTooltip({
        trigger: "axis",
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const p = params[0] as { axisValue: string; value: number | null };
          const val = p.value;
          const formatted = val != null ? val.toFixed(6) : "N/A";
          return `<div style="font-weight:500;margin-bottom:4px;color:#9ca3af;">${formatTooltipDate(p.axisValue)}</div>
            <div style="font-weight:600;color:#38bdf8;">${logScale ? "ln " : ""}${formatted}</div>`;
        },
      }),
      grid: createGrid({
        top: 8,
        right: 12,
        bottom: 50,
        left: 12,
        containLabel: true,
      }),
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          show: true,
          bottom: 8,
          height: 18,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af", fontSize: 9 },
          brushSelect: false,
          labelFormatter: (_value: number, valueStr: string) =>
            formatTooltipDate(valueStr),
        },
      ],
      xAxis: {
        ...createXAxis({ data: dates, boundaryGap: false }),
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
          formatter: (val: string) => formatDateLabel(val, "short"),
        },
      },
      yAxis: createYAxis({
        formatter: (v: number) => (logScale ? v.toFixed(2) : v.toFixed(1)),
      }),
      series: [
        createLineSeries(
          {
            name: "Price Ratio",
            data: values as (number | null)[],
            color: CHART_COLORS.series[0],
            width: 1.5,
            smooth: false,
            showSymbol: false,
            areaStyle: true,
          },
          0,
        ),
      ],
    } as EChartsOption;
  }, [dates, values, logScale]);

  const formattedValue = currentValue != null ? currentValue.toFixed(6) : "—";

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error || dates.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <TrendingUp className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No ratio data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300 truncate">
            Price Ratio
          </span>
          <span className="text-xs font-semibold tabular-nums text-sky-400">
            {logScale ? `ln ${formattedValue}` : formattedValue}
          </span>
        </div>
        <button
          onClick={() => setLogScale((v) => !v)}
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
            logScale
              ? "bg-sky-500/20 text-sky-300 border border-sky-500/40"
              : "bg-slate-700/50 text-slate-400 border border-slate-600/50 hover:text-slate-300"
          }`}
        >
          LOG
        </button>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <EChartsRenderer
          chartOption={chartOption}
          height="100%"
          hasData={dates.length > 0}
          containerClassName=""
        />
      </div>
    </div>
  );
};

export default SpreadRatioWidget;
